import Aria from "../../util/Aria";
import { functionToPromiseWrapper } from "../../util/PromiseWrapper";
import "../../lib/RichGenerator";
import { Timeout } from "../../util/Misc";
import { getChatEventInfo, getContentSeenEvent, getCustomerRespondedEvent, getEndedEvent, getStartedEvent } from "./controller/SurveyEventManager";
import { handleCommandEvent } from "../../controller/TranscriptController";
import {surveyRender} from "../templates/renders/survey";

/* eslint-disable no-param-reassign */

const SurveyView: any = {
    tagName: "survey-view",
    initialize(options) {
        initComponent(options);
    },
    renderer: surveyRender,
    created() {
        this.quesCache = {};
        this.sdkInstance = this.$app.getSDKInstance();
        this.surveyJson = {};
        this.ariaUtil = new Aria(this.$app);
    },
    removed() {
        if (this.richInstance) {
            this.richInstance.clearAll();
            this.richInstance = null;
        }

        // Set state to hidden in case of instance removal
        this.setSurveyStates(false);
    },
    render() {
        const uiConfig = this.$app.getUIConfig();
        return new Promise((resolve, reject) => { // eslint-disable-line  no-unused-vars
            if (uiConfig.pcsID) {
                this.chatEventObject = this.getEventDefaultParams();

                functionToPromiseWrapper(window.messagingApp.loadConfig, uiConfig.skinPath, `${uiConfig.pcsID}.json`).then(data => {
                    this.surveyJson = data;
                    this.invokeRichEngine(data, false, transition => {
                        if (transition.closeChat) {
                            this.$app.closeChat();
                        } else if (transition.engage){
                            resolve(transition.engage);
                        }
                        if (transition.command?.event) {
                            handleCommandEvent(transition.command.event, this.$app.getSDKInstance())
                        }
                    });
                }).catch(() => {
                    resolve(undefined);
                });
            } else {
                resolve(undefined);
            }

            this.testClick = function () {
                resolve(undefined);
            };
        });
    },

    setSurveyStates(isVisible, evt) {
        this.$app.getStateMgr().setStateItem("survey.visible", isVisible);

        if (evt) {
            this.$app.getStateMgr().setStateItem("survey.evt", evt);
        }
    },

    renderPostChatSurvey(pcsId, data, asyncData) {
        this.$app.setChatDoneState(true);
        const uiConfig = this.$app.getUIConfig();
        this.chatEventObject = data || this.getEventDefaultParams();

        if(!this.chatEventObject.chatID) {
            this.chatEventObject.chatID = this.chatEventObject.engagementID;
        }

        if (asyncData) {
            this.chatEventObject.siteID = asyncData.siteID;
            this.chatEventObject.businessUnitID = asyncData.businessUnitID;
            this.chatEventObject.agentGroupID = asyncData.agentGroupID;
            this.chatEventObject.agentID = asyncData.agentID;
            this.chatEventObject.availableAgentAttributes = "";
            this.chatEventObject.customerID = asyncData.customerID;
            this.chatEventObject.conversationID = asyncData.conversationID;
            this.chatEventObject.initialCustomerID = asyncData.customerID;
            this.chatEventObject.chatID = asyncData.engagementID;
        }

        functionToPromiseWrapper(window.messagingApp.loadConfig, uiConfig.skinPath, `${pcsId}.json`).then(dataRes => {
            this.surveyJson = dataRes;           
            this.invokeRichEngine(dataRes, true, transition => {
                const close = transition.closeChat;

                if (transition.survey) {
                    this.submitPostChatSurveyData(transition);
                }
                if (transition.command?.event) {
                    handleCommandEvent(transition.command.event, this.$app.getSDKInstance())
                }

                if (close) {
                    this.richInstance.clearAll();
                    this.richInstance = null;
                    this.$app.closeChat();

                    if (this.sdkInstance.isPersistentWindow()) {
                        Timeout.instantiate(() => {
                            window.parent.close();
                        }, 200);
                    }
                }
            });
        }).catch(() => {
        });
    },
    invokeRichEngine(data, isPost, cb) {
        if (window.RichMain) {
            this.listenForRichEnginePluginsRendered();
            this.richInstance = window.RichMain.instance;
            this.richInstance.renderSurveyContent({
                spec: data,
                viewStartedCb: isPost ? this.firePostChatStartedEvent.bind(this) : this.firePreChatStartedEvent.bind(this),
                viewChangeCb: this.fireCustomerRespondedEvent.bind(this),
                viewEndedCb: this.fireAutomationEndedEvent.bind(this),
                container: this.el.firstElementChild,
                CIAPI: this.sdkInstance,
                viewRenderedCb: this.announceRichAriaOutputs.bind(this),
            });
            this.richInstance.registerCBListener(params => {
                cb(params);
            });
        }
    },

    listenForRichEnginePluginsRendered() {
        const surveyArea = this.el.firstElementChild;
        surveyArea.addEventListener("rich-eng:plugins-rendered", () => {
            // if none of the survey controls are focused, focus the survey container.
            if (!surveyArea.contains(document.activeElement)) {
                surveyArea.focus();
            }
        });
    },

    /**
     * Receives / filter and announce richEngine aria outputs.
     * For survey we only announce outputs with accessibilityReader flags.
     *
     * Note:
     * Surveys contain display logic and content cannot simply be read
     * as soon as the view is rendered. This accessibilityReader flag
     * hack is only here until we develop a better solition.
     *
     * @param {Array.<Object>} richAriaOutputs - array of aria outputs
     */
    announceRichAriaOutputs(richAriaOutputs) {
        const readerRichAriaOutputs = richAriaOutputs.filter(
            ariaOut => ariaOut.accessibilityReader
        );
        this.ariaUtil.announceRichDataMessage(readerRichAriaOutputs);
    },

    firePreChatStartedEvent(nodeID, name, questions) {
        const reqEvent = {
            _domain: "engagement",
            evt: "requested",
            automatonType: "rich-engine",
            resourceNeeded: "automaton",
            clientTimestamp: Date.now(),
        };

        Object.assign(reqEvent, this.chatEventObject);

        const startedEvent = getStartedEvent(this.surveyJson,
            `br,${this.chatEventObject.businessRuleID}`, "chat",
            this.chatEventObject, this.surveyJson.widgetType || "guide");
        const content = this.contentSendToCustomer(this.surveyJson, nodeID, name, questions);

        if (this.surveyJson.widgetID) {
            reqEvent["automaton.automatonID"] = this.surveyJson.widgetID;
        }

        if (this.surveyJson.widgetName) {
            reqEvent["automatonName"] = this.surveyJson.widgetName;
        }

        this.sdkInstance.logEventToDW({ eventList: [reqEvent, startedEvent, content] });

        // Update states
        this.setSurveyStates(true, startedEvent.evt);
    },
    firePostChatStartedEvent(nodeID, name, questions) {
        //satisfaction survey is the predefined value which data warehouse looks when parsing the post chat survey event data
        this.surveyJson.widgetType = "satisfactionSurvey";
        const startedEvent = getStartedEvent(this.surveyJson, "survey,survey", "chat",
            this.chatEventObject, this.surveyJson.widgetType);
        const content = this.contentSendToCustomer(this.surveyJson, nodeID, name, questions);

        this.sdkInstance.logEventToDW({ eventList: [startedEvent, content] });

        // Update states
        this.setSurveyStates(true, startedEvent.evt);
    },
    fireCustomerRespondedEvent(oNodeID, oNodeName, oQuestions, oAns, nodeID, nNodeName, nQuestions) {
        const resp = this.getCustomerResponded(this.surveyJson, oNodeID, oNodeName, oQuestions, oAns);
        const content = this.contentSendToCustomer(this.surveyJson, nodeID, nNodeName, nQuestions);
        this.sdkInstance.logEventToDW({ eventList: [resp, content] });
    },
    fireAutomationEndedEvent(nodeID, oNodeName, completed, outcome, oQuestions, oAns, isAbandoned) {
        const resp = this.getCustomerResponded(this.surveyJson, nodeID, oNodeName, oQuestions, oAns);
        const endedEvent = getEndedEvent(this.surveyJson, completed, outcome, isAbandoned, this.chatEventObject, this.surveyJson.widgetType);

        this.sdkInstance.logEventToDW({ eventList: [resp, endedEvent] });

        this.surveyJson = {};

        // Update states
        this.setSurveyStates(false, endedEvent.evt);
    },

    getEventDefaultParams() {
        /**
         * @type {com.inq.flash.client.data.Chat} chatParams
         */
        return getChatEventInfo(this.sdkInstance);
    },

    submitPostChatSurveyData(sparmas) {
        const data = {
            engagementId: this.chatEventObject.chatID,
            siteId: parseInt(this.chatEventObject.siteID, 10),
            surveyQuestions: []
        };
        let q;
        Object.keys(sparmas.survey).forEach(key => {
            q = { id: key };

            if (this.quesCache[key] && sparmas.survey[key]) {
                q["question"] = this.quesCache[key];
                q.answer = String(sparmas.survey[key]);
                data.surveyQuestions.push(q);
            }
        });
        delete sparmas.survey;
        delete sparmas.closeChat;

        Object.keys(sparmas).forEach(key => {
            data[key] = sparmas[key];
        });
        if (!sparmas.surveyAlerts) {
            data["surveyAlerts"] = [];
        }

        this.sdkInstance.submitNativePostChatSurvey(data);
    },

    contentSendToCustomer(jsonSpec, nodeID, name, questions) {
        const ques = [];
        const qIds = [];

        questions.forEach(({ id, lbl }) => {
            qIds.push(id);
            ques.push(lbl);
            this.quesCache[id] = lbl;
        });

        return getContentSeenEvent(jsonSpec, nodeID, name, ques, qIds, this.chatEventObject, jsonSpec.widgetType);
    },

    getCustomerResponded(jsonSpec, oNodeID, name, oQues, oAns) {
        // let quesIDS = Object.keys(oQues);
        return getCustomerRespondedEvent(jsonSpec, oNodeID, name, oQues, oAns, this.chatEventObject, jsonSpec.widgetType);
    },

    closeChat() {
        if (this.richInstance) {
            this.richInstance.fireCloseSurveyEvent();
        }
    },
};

export default appInstance => {
    SurveyView.$app = appInstance;
    SurveyView.constants = {};

    return SurveyView;
};

const initComponent = (options: any) => {
    const appInstance = options.$app;
    const config = appInstance.getUIConfig();
    const constObj: any = options.constants;

    if (config.surveyContainerStyles) {
        constObj.style = config.surveyContainerStyles;
    }

    constObj.extendedSurveyTopBarVisible = appInstance.extendedSurveyTopBarVisible;
    constObj.extendedSurveyFooterVisible = appInstance.extendedSurveyFooterVisible;
};
